import { useState, useRef, useEffect } from 'react';
import { IoChevronUpOutline } from 'react-icons/io5';

interface Option {
  value: string;
  label: string;
}

interface FilterDropdownProps {
  options: Option[];
  defaultValue?: string;
  defaultLabel?: string;
  icon?: React.ReactNode;
  onChange?: (value: string) => void;
}

export const FilterDropdown = ({ options, defaultValue = '', defaultLabel = '', icon, onChange }: FilterDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find the current option based on defaultValue
  const currentOption = options.find(opt => opt.value === defaultValue);
  const displayLabel = currentOption ? currentOption.label : defaultLabel;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: Option) => {
    setIsOpen(false);
    onChange?.(option.value);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="h-10 rounded-xl bg-white border border-[rgba(112,115,147,0.16)] text-[#556DF4] pl-4 pr-4 py-[7px] cursor-pointer transition-all hover:bg-[#F0F1F4] flex items-center gap-2 text-[15px] font-['Arcadia_Text',system-ui,sans-serif] min-w-[140px]"
      >
        <div className="flex items-center gap-2 flex-1">
          {icon}
          <span className="text-gray-700">{displayLabel}</span>
        </div>
        <IoChevronUpOutline className={`text-[rgb(54,54,68)] transition-transform ${isOpen ? '' : 'rotate-180'}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-full bg-white rounded-xl border-[0.5px] border-[rgba(112,115,147,0.16)] shadow-[0_2px_8px_rgba(0,0,0,0.05)] py-2 z-50">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelect(option)}
              className={`w-full px-4 py-2 text-left hover:bg-[#F0F1F4] flex items-center gap-2 ${
                option.value === defaultValue ? 'text-[#556DF4]' : 'text-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}; 